import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { X, RotateCcw, Check } from "lucide-react";

/**
 * Lightweight crop + zoom modal for the admin product photo upload.
 * Props:
 *   file:      File | null    — the image to crop; when non-null, modal shows
 *   onCancel:  () => void     — user closed without saving
 *   onSave:    (croppedFile: File) => void  — user saved cropped result
 */

async function readImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedFile(imageSrc, pixelCrop, filename) {
  const img = await readImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  // Cap max output side so uploads stay lean; backend also compresses further.
  const MAX = 1800;
  const scale = Math.min(1, MAX / Math.max(pixelCrop.width, pixelCrop.height));
  canvas.width = Math.round(pixelCrop.width * scale);
  canvas.height = Math.round(pixelCrop.height * scale);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, canvas.width, canvas.height,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const name = (filename || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      resolve(new File([blob], name, { type: "image/jpeg" }));
    }, "image/jpeg", 0.9);
  });
}

const DEFAULT_ASPECTS = [
  { label: "4:5", value: 4 / 5 },
  { label: "1:1", value: 1 },
  { label: "Free", value: null },
];

export default function CropModal({ file, onCancel, onSave, aspects, title }) {
  const options = aspects && aspects.length > 0 ? aspects : DEFAULT_ASPECTS;
  const [srcUrl, setSrcUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(options[0].value);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  // Rebuild the object URL whenever `file` changes; revoke on unmount / change.
  React.useEffect(() => {
    if (!file) { setSrcUrl(null); return; }
    const url = URL.createObjectURL(file);
    setSrcUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setPixels(null);
    setAspect(options[0].value);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setPixels(areaPixels);
  }, []);

  const reset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const save = async () => {
    if (!srcUrl || !pixels) return;
    setBusy(true);
    try {
      const cropped = await getCroppedFile(srcUrl, pixels, file.name);
      onSave(cropped);
    } finally {
      setBusy(false);
    }
  };

  if (!file) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-sj-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="crop-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-md max-h-[95vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-sj-ink/10 flex items-center justify-between">
          <div>
            <div className="overline">{title || "Crop & resize"}</div>
            <div className="text-sm text-sj-muted mt-0.5 truncate max-w-[280px]">{file.name}</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-sj-muted hover:text-sj-ink"
            aria-label="Close"
            data-testid="crop-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Cropper canvas */}
        <div className="relative w-full bg-sj-cream" style={{ height: "min(60vh, 480px)" }}>
          {srcUrl && (
            <Cropper
              image={srcUrl}
              crop={crop}
              zoom={zoom}
              aspect={aspect || undefined}
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={true}
              objectFit="contain"
            />
          )}
        </div>

        {/* Controls */}
        <div className="p-5 space-y-4 border-t border-sj-ink/10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-sj-inkSoft mr-1">Aspect:</span>
            {options.map((a) => (
              <button
                key={a.label}
                type="button"
                onClick={() => setAspect(a.value)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  aspect === a.value
                    ? "bg-sj-ink text-white border-sj-ink"
                    : "bg-white text-sj-ink border-sj-ink/15 hover:border-sj-goldDark hover:text-sj-goldDark"
                }`}
                data-testid={`crop-aspect-${a.label}`}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              onClick={reset}
              className="ml-auto text-xs text-sj-inkSoft hover:text-sj-goldDark inline-flex items-center gap-1"
              data-testid="crop-reset"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-sj-inkSoft w-14 shrink-0">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-sj-gold"
              data-testid="crop-zoom"
              aria-label="Zoom"
            />
            <span className="text-xs text-sj-muted w-10 text-right tabular-nums">{zoom.toFixed(2)}x</span>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-sj-ink/10 flex items-center justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onCancel}
            className="btn-outline text-sm"
            disabled={busy}
            data-testid="crop-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || !pixels}
            className="btn-primary text-sm disabled:opacity-60"
            data-testid="crop-save"
          >
            <Check size={14} /> {busy ? "Saving…" : "Save crop"}
          </button>
        </div>
      </div>
    </div>
  );
}
